import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {Horses} from '../../../../../../@shared/services/horses';
import {AQH_HOR_1, AQH_HOR_2, AQH_HOR_4} from '../../../../../../@shared/models/input.module';
import {HorseResponse} from '../../../../../../@shared/models/response.module';
import {toYMD} from '../../../../../../@core/utils/utils.service';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {ToastService} from '../../../../../../@core/services/toast.service';
import {TranslateService} from '@ngx-translate/core';

function formatDateyyyyMMdd(date_of_birth: Date | null) {
  if (!date_of_birth) return null;
  const d = new Date(date_of_birth);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

@Component({
  selector: 'app-edit-horse',
  standalone: false,
  templateUrl: './edit-horse.html',
  styleUrl: './edit-horse.css'
})
export class EditHorse implements OnChanges {
  @Input() horse: HorseResponse

  horseRegistries: { label: string; value: number }[] = [];
  horseGenders: { label: string; value: number }[] = [];

  name = '';
  date_of_birth = ""
  passport_id = '';
  breed = '';
  color = '';
  vaccination_date = ""
  next_vaccination_date = ""

  selectedRegistry: any = null;
  selectedGender: any = null;

  horseImageFile: File | null = null;
  pedigreeFile: File | null = null;
  vaccinationCardFile: File | null = null;

  // Image preview URLs (for newly selected files)
  horseImagePreview: string | null = null;
  pedigreePreview: string | null = null;
  vaccinationCardPreview: string | null = null;

  // Validation state
  submitted = false;
  saving = false;

  constructor(private horseService: Horses,
              private modal: NgbActiveModal,
              private toastService: ToastService,
              private translateService: TranslateService) {
  }

  private initDropdowns() {
    this.horseRegistries = [
      { label: 'AQHA', value: 0 },
      { label: 'APHA', value: 1 },
      { label: 'ApHC', value: 2 },
      { label: this.translateService.instant('horses.registry.other'), value: 3 },
    ];
    this.horseGenders = [
      { label: this.translateService.instant('horses.gender.gelding'), value: 0 },
      { label: this.translateService.instant('horses.gender.stallion'), value: 1 },
      { label: this.translateService.instant('horses.gender.mare'), value: 2 },
    ];
  }

  // Validation helpers
  get isNameInvalid(): boolean {
    return this.submitted && !this.name.trim();
  }

  get isGenderInvalid(): boolean {
    return this.submitted && (this.selectedGender === null || this.selectedGender === undefined);
  }

  get isDateOfBirthInvalid(): boolean {
    return this.submitted && !this.date_of_birth;
  }

  get isPassportIdInvalid(): boolean {
    return this.submitted && !this.passport_id.trim();
  }

  get isFormValid(): boolean {
    return !!(
      this.name.trim() &&
      this.selectedGender !== null &&
      this.selectedGender !== undefined &&
      this.date_of_birth &&
      this.passport_id.trim()
    );
  }

  ngOnInit() {
    this.initDropdowns();
    this.translateService.onLangChange.subscribe(() => {
      this.initDropdowns();
    });

    if (this.horse) {
      this.name = this.horse.name ?? '';
      this.date_of_birth = toYMD(this.horse.date_of_birth);
      this.passport_id = this.horse.passport_id ?? '';
      this.breed = this.horse.breed ?? '';
      this.color = this.horse.color ?? '';
      this.vaccination_date = toYMD(this.horse.vaccination_date);
      this.next_vaccination_date = toYMD(this.horse.next_vaccination_date);
      this.selectedRegistry = this.horse.horse_registry_id;
      this.selectedGender = this.horse.gender;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.horse) {
      this.name = this.horse.name ?? '';
      this.date_of_birth = toYMD(this.horse.date_of_birth);
      this.passport_id = this.horse.passport_id ?? '';
      this.breed = this.horse.breed ?? '';
      this.color = this.horse.color ?? '';
      this.vaccination_date = toYMD(this.horse.vaccination_date);
      this.next_vaccination_date = toYMD(this.horse.next_vaccination_date);
      this.selectedRegistry = this.horse.horse_registry_id;
      this.selectedGender = this.horse.gender;
    }
  }

  private toISO(date: string): string {
    if (!date) return '';
    // Handle full ISO strings (2025-10-12T00:00:00.000Z)
    if (date.includes('T')) {
      return date.split('T')[0]; // → "2025-10-12"
    }
    // Already YYYY-MM-DD (from input type="date")
    return date;
  }

  onSave() {
    this.submitted = true;

    if (!this.isFormValid) {
      this.toastService.error(this.translateService.instant('horses.form.validationError'));
      return;
    }

    this.saving = true;

    // Don't send image URLs - backend preserves existing values
    // Images are uploaded separately via uploadHorseImage (AQH_HOR_4)
    const payload: AQH_HOR_2 = {
      horse_id: this.horse.horse_id,
      name: this.name.trim(),
      date_of_birth: this.date_of_birth!,
      passport_id: this.passport_id.trim(),
      horse_registry_id: this.selectedRegistry?.value ?? this.selectedRegistry ?? 0,
      gender: this.selectedGender,
      breed: this.breed.trim(),
      color: this.color.trim(),
      vaccination_date: this.vaccination_date!,
      next_vaccination_date: this.next_vaccination_date!,
    };

    this.horseService.editHorse(payload).subscribe({
      next: (res) => {
        let horse = res;

        // Upload images sequentially
        const uploadImages = async () => {
          if (this.horseImageFile) {
            try {
              const imgPayload: AQH_HOR_4 = {
                horse_id: res.horse_id,
                image_type: 'horse_image',
                image: this.horseImageFile
              };
              horse = await this.horseService.uploadHorseImage(imgPayload).toPromise() || horse;
            } catch (e) {
              console.error('Horse image upload failed:', e);
            }
          }
          if (this.pedigreeFile) {
            try {
              const pedigreePayload: AQH_HOR_4 = {
                horse_id: res.horse_id,
                image_type: 'pedigree_image',
                image: this.pedigreeFile
              };
              horse = await this.horseService.uploadHorseImage(pedigreePayload).toPromise() || horse;
            } catch (e) {
              console.error('Pedigree image upload failed:', e);
            }
          }
          if (this.vaccinationCardFile) {
            try {
              const vaccPayload: AQH_HOR_4 = {
                horse_id: res.horse_id,
                image_type: 'vaccination_image',
                image: this.vaccinationCardFile
              };
              horse = await this.horseService.uploadHorseImage(vaccPayload).toPromise() || horse;
            } catch (e) {
              console.error('Vaccination image upload failed:', e);
            }
          }

          this.saving = false;
          this.toastService.success(this.translateService.instant('horses.form.updateSuccess'));
          this.modal.close(true);
        };

        uploadImages();
      },
      error: (err) => {
        this.saving = false;
        this.toastService.error(err.error?.reason || this.translateService.instant('horses.form.updateError'));
      }
    });
  }

  close() {
    this.modal.close();
  }

  // Image preview getters - show new file preview or existing image
  get currentHorseImage(): string | null {
    return this.horseImagePreview || this.horse?.image_url || null;
  }

  get currentPedigreeImage(): string | null {
    return this.pedigreePreview || this.horse?.pedigree_image_url || null;
  }

  get currentVaccinationCard(): string | null {
    return this.vaccinationCardPreview || this.horse?.vaccination_card_url || null;
  }

  onHorseImageSelected(files: File[]) {
    this.horseImageFile = files[0] || null;
    this.horseImagePreview = this.horseImageFile ? URL.createObjectURL(this.horseImageFile) : null;
  }

  onPedigreeSelected(files: File[]) {
    this.pedigreeFile = files[0] || null;
    this.pedigreePreview = this.pedigreeFile ? URL.createObjectURL(this.pedigreeFile) : null;
  }

  onVaccinationCardSelected(files: File[]) {
    this.vaccinationCardFile = files[0] || null;
    this.vaccinationCardPreview = this.vaccinationCardFile ? URL.createObjectURL(this.vaccinationCardFile) : null;
  }

  clearHorseImageSelection() {
    this.horseImageFile = null;
    this.horseImagePreview = null;
  }

  clearPedigreeSelection() {
    this.pedigreeFile = null;
    this.pedigreePreview = null;
  }

  clearVaccinationCardSelection() {
    this.vaccinationCardFile = null;
    this.vaccinationCardPreview = null;
  }
}

function fromApiDate(v?: string | null): string {
  if (!v) return ''; // always return a string
  try {
    const parsed = new Date(v);
    if (isNaN(parsed.getTime())) {
      const [datePart] = v.split(',');
      const [month, day, year] = datePart.split('/').map(x => x.trim());
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    const y = parsed.getFullYear();
    const m = String(parsed.getMonth() + 1).padStart(2, '0');
    const d = String(parsed.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  } catch {
    return '';
  }
}
